import React, { useState, useCallback } from 'react';
import { QRCodeCanvas } from 'qrcode.react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_KEY = process.env.REACT_APP_API_KEY || '';

const QR_STYLES = [
  { id: 'gemba',   label: 'GembaDocs', fg: '#0679ff', bg: '#e8f1ff' },
  { id: 'orange',  label: 'Orange',    fg: '#ffffff',  bg: '#0679ff' },
  { id: 'navy',    label: 'Navy',      fg: '#ffffff',  bg: '#0560cc' },
  { id: 'fresh',   label: 'Fresh',     fg: '#0679ff',  bg: '#ffffff' },
  { id: 'dark',    label: 'Dark',      fg: '#0679ff',  bg: '#111c2d' },
  { id: 'classic', label: 'Classic',   fg: '#000000',  bg: '#ffffff' },
];

function isValidUrl(str) {
  try { new URL(/^https?:\/\//i.test(str) ? str : 'https://' + str); return true; }
  catch { return false; }
}

async function apiShorten(originalUrl) {
  const res = await fetch(`${API_URL}/shorten`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
    body: JSON.stringify({ originalUrl }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Failed to shorten URL');
  return data.data;
}

export default function App() {
  const [url, setUrl]               = useState('');
  const [result, setResult]         = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [copied, setCopied]         = useState(false);
  const [selectedStyle, setSelectedStyle] = useState('gemba');
  const [qrSize, setQrSize]         = useState(260);

  const handleShorten = useCallback(async () => {
    const trimmed = url.trim();
    if (!trimmed) return setError('Please paste a URL first.');
    if (!isValidUrl(trimmed)) return setError("That doesn't look like a valid URL.");
    setError(''); setLoading(true); setResult(null);
    try {
      const data = await apiShorten(trimmed);
      setResult(data);
      setSelectedStyle('gemba');
      setTimeout(() => {
        document.getElementById('result-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [url]);

  const handleCopy = useCallback(async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  const handleDownloadQR = useCallback(() => {
    const canvas = document.querySelector('#qr-canvas canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `gembatiny-qr-${result?.shortCode || 'code'}-${selectedStyle}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }, [result, selectedStyle]);

  const cs = QR_STYLES.find(s => s.id === selectedStyle);

  return (
    <div style={S.root}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;500;600;700;800&family=Montserrat:wght@600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
        textarea:focus{border-color:#0679ff!important;box-shadow:0 0 0 3px rgba(6,121,255,.1)!important}
        .sty-btn:hover{transform:scale(1.05)!important}
        .copy-btn:hover{background:#0560cc!important}
        .btn-primary:hover:not(:disabled){background:#0560cc!important;transform:translateY(-1px);box-shadow:0 5px 16px rgba(6,121,255,.4)!important}
        .dl-btn:hover{background:#0560cc!important;transform:translateY(-1px)}
      `}</style>

      {/* HERO */}
      <div style={S.hero}>
        <div style={S.heroLogo}>
          <div>
            <div style={S.heroName}>Gemba<em style={{color:'#f26522',fontStyle:'normal'}}>Tiny</em></div>
            <div style={S.heroTag}>URL Shortener</div>
          </div>
        </div>
        <p style={S.heroSub}>Shorten any link instantly. Generate branded QR codes for SOPs, Kanban cards, and process documents.</p>
      </div>

      {/* MAIN */}
      <div style={S.main}>

        {/* INPUT CARD */}
        <div style={S.card}>
          <div style={S.cardTitle}>Shorten a URL &amp; Generate QR Code</div>
          <div style={S.cardSub}>Paste your long link below to generate a short, shareable URL and QR code.</div>
          <label style={S.lbl}>Long URL</label>
          <textarea
            style={S.textarea}
            value={url}
            onChange={e => { setUrl(e.target.value); setError(''); }}
            placeholder="https://www.amazon.com/Sony-WH-CH720N-Canceling-Headphones-Microphone/dp/B0BS1QCFHX"
            rows={3}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleShorten(); }}
          />
          {error && <p style={S.err}>{error}</p>}
          <button
            className="btn-primary"
            style={{ ...S.btn, opacity: loading || !url.trim() ? 0.45 : 1, cursor: loading || !url.trim() ? 'not-allowed' : 'pointer' }}
            onClick={handleShorten}
            disabled={loading || !url.trim()}
          >
            {loading ? <span style={S.spinner} /> : (
              <>
                <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{verticalAlign:'middle',marginRight:6}}>
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
                Generate Short URL
              </>
            )}
          </button>
          <p style={S.hint}>Ctrl + Enter to shorten · Secured with API key</p>

          {/* QR hint — only shown before result */}
          {!result && (
            <div style={S.qrHint}>
              <svg width={28} height={28} viewBox="0 0 24 24" fill="none" style={{flexShrink:0,opacity:.4}} stroke="#0679ff" strokeWidth="1.5">
                <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="3" height="3"/>
                <rect x="19" y="14" width="2" height="2"/><rect x="14" y="19" width="2" height="2"/><rect x="18" y="18" width="3" height="3"/>
              </svg>
              <p style={{fontSize:12,color:'#8fa0bb',lineHeight:1.5,margin:0}}>
                <strong style={{color:'#5e748f',fontWeight:600}}>QR code will appear here</strong> once you shorten your link — you can customise the style and download it as a PNG.
              </p>
            </div>
          )}
        </div>

        {/* RESULT */}
        {result && (
          <div id="result-section" style={{display:'flex',flexDirection:'column',gap:18,animation:'fadeUp .4s ease both'}}>

            {/* Short URL card */}
            <div style={{...S.card, borderTop:'3px solid #1a9e5c', marginBottom:0}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:8}}>
                <span style={S.okBadge}><span style={S.okDot}/>Link Created</span>
                <span style={{fontSize:12,color:'#8fa0bb'}}>{new Date(result.createdAt).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}</span>
              </div>
              <div style={S.urlRow}>
                <span style={S.shortUrl}>{result.shortUrl}</span>
                <button className="copy-btn" onClick={() => handleCopy(result.shortUrl)}
                  style={{...S.copyBtn, background: copied ? '#1a9e5c' : '#0679ff'}}>
                  {copied
                    ? <><svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>Copied!</>
                    : <><svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>Copy URL</>
                  }
                </button>
              </div>
              <div style={{display:'flex',gap:8,alignItems:'flex-start'}}>
                <span style={S.origLbl}>Original</span>
                <span style={S.origUrl}>{result.originalUrl}</span>
              </div>
              <div style={S.statsRow}>
                {[['Clicks','0'],['Code',result.shortCode],['Created',new Date(result.createdAt).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})]].map(([l,v])=>(
                  <div key={l} style={S.stat}>
                    <span style={S.statLbl}>{l}</span>
                    <span style={S.statVal}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* QR Card */}
            <div style={{...S.card, alignItems:'center', display:'flex', flexDirection:'column', marginBottom:0}}>
              <div style={{textAlign:'center',marginBottom:22}}>
                <h2 style={{fontFamily:"'Montserrat',sans-serif",fontSize:20,fontWeight:800,color:'#0679ff',marginBottom:4}}>QR Code</h2>
                <p style={{fontSize:13,color:'#5e748f',lineHeight:1.5}}>Choose a style and download as PNG — perfect for printed SOPs &amp; Kanban cards</p>
              </div>

              {/* Style picker */}
              <div style={{display:'flex',gap:8,flexWrap:'wrap',justifyContent:'center',marginBottom:24}}>
                {QR_STYLES.map(s => (
                  <button key={s.id} className="sty-btn" onClick={() => setSelectedStyle(s.id)}
                    style={{background:s.bg,border:`2px solid ${selectedStyle===s.id?s.fg:'transparent'}`,borderRadius:8,padding:'8px 13px',cursor:'pointer',display:'flex',alignItems:'center',gap:6,minWidth:86,justifyContent:'center',transition:'all .2s',transform:selectedStyle===s.id?'scale(1.08)':'scale(1)',boxShadow:selectedStyle===s.id?'0 4px 14px rgba(0,0,0,.18)':'none',fontFamily:"'Open Sans',sans-serif"}}>
                    <div style={{width:7,height:7,borderRadius:'50%',background:s.fg,flexShrink:0}}/>
                    <span style={{fontSize:11,fontWeight:700,color:s.fg}}>{s.label}</span>
                  </button>
                ))}
              </div>

              {/* QR Preview */}
              <div id="qr-canvas" style={{background:cs.bg,borderRadius:12,padding:18,marginBottom:18,boxShadow:`0 6px 28px rgba(6,121,255,.2)`,transition:'all .3s ease'}}>
                <QRCodeCanvas value={result.shortUrl} size={qrSize} fgColor={cs.fg} bgColor={cs.bg} level="H" includeMargin={true}/>
              </div>

              {/* Size slider */}
              <div style={{display:'flex',alignItems:'center',gap:14,width:'100%',maxWidth:360,marginBottom:20}}>
                <span style={{fontSize:12,fontWeight:600,color:'#5e748f',whiteSpace:'nowrap',minWidth:86}}>Size: {qrSize}px</span>
                <input type="range" min={160} max={420} step={20} value={qrSize} onChange={e=>setQrSize(Number(e.target.value))} style={{flex:1,accentColor:'#0679ff',cursor:'pointer'}}/>
              </div>

              {/* Divider */}
              <div style={{display:'flex',alignItems:'center',gap:12,width:'100%',maxWidth:380,marginBottom:18}}>
                <div style={{flex:1,height:1,background:'#e8f1ff'}}/>
                <span style={{fontSize:10,fontWeight:700,color:'#8fa0bb',letterSpacing:'.1em',textTransform:'uppercase',whiteSpace:'nowrap'}}>Download</span>
                <div style={{flex:1,height:1,background:'#e8f1ff'}}/>
              </div>

              {/* Download button */}
              <button className="dl-btn" onClick={handleDownloadQR}
                style={{background:'#0679ff',color:'#fff',border:'none',borderRadius:8,padding:'13px 26px',fontSize:14,fontWeight:700,fontFamily:"'Open Sans',sans-serif",cursor:'pointer',display:'flex',alignItems:'center',gap:9,boxShadow:'0 3px 12px rgba(6,121,255,.3)',transition:'background .2s,transform .2s'}}>
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download QR — {cs.label} Style
              </button>
            </div>

          </div>
        )}
      </div>

      {/* FOOTER */}
      <div style={S.footer}>
        <p style={{fontSize:11,letterSpacing:'.12em',textTransform:'uppercase',color:'rgba(255,255,255,.35)',marginBottom:14,fontWeight:600}}>Powered by</p>
        <a href="https://gembadocs.com" target="_blank" rel="noreferrer" style={{display:'inline-block',marginBottom:6}}>
          <img src="https://gembadocs.com/front_new/images/gemba_landscap_logo_white.webp" alt="GembaDocs"
            style={{height:42,display:'block',margin:'0 auto'}}
            onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='block'}}/>
          <span style={{display:'none',fontFamily:"'Montserrat',sans-serif",fontSize:22,fontWeight:800,color:'#fff',letterSpacing:'-.5px'}}>
            Gemba<span style={{color:'#f26522'}}>Docs</span>
          </span>
        </a>
        <div style={{width:36,height:2,background:'#f26522',margin:'12px auto'}}/>
        <p style={{color:'rgba(255,255,255,.55)',fontSize:13}}>© 2025 GembaDocs Limited · <a href="https://gembadocs.com/privacy-policy" target="_blank" rel="noreferrer" style={{color:'rgba(255,255,255,.75)',textDecoration:'none'}}>Privacy Policy</a> · <a href="https://gembadocs.com/terms-conditions" target="_blank" rel="noreferrer" style={{color:'rgba(255,255,255,.75)',textDecoration:'none'}}>Terms</a></p>
        <p style={{marginTop:6,fontSize:12,color:'rgba(255,255,255,.4)'}}>GembaTiny — URL Shortener powered by GembaDocs</p>
      </div>
    </div>
  );
}

const S = {
  root:     { minHeight:'100vh', background:'#f5f7fa', fontFamily:"'Open Sans',sans-serif", color:'#111c2d' },
  hero:     { background:'#fff', borderBottom:'3px solid #0679ff', padding:'40px 24px 48px', textAlign:'center' },
  heroLogo: { display:'flex', alignItems:'center', justifyContent:'center', gap:16, marginBottom:24 },
  heroName: { fontFamily:"'Montserrat',sans-serif", fontSize:26, fontWeight:800, color:'#1a3355', lineHeight:1, letterSpacing:'-.5px' },
  heroTag:  { fontSize:11, fontWeight:600, color:'#8fa0bb', letterSpacing:'.1em', textTransform:'uppercase', marginTop:4 },
  heroSub:  { color:'#5e748f', fontSize:15, maxWidth:480, margin:'0 auto', lineHeight:1.65 },
  main:     { maxWidth:700, margin:'0 auto', padding:'28px 20px 72px' },
  card:     { background:'#fff', borderRadius:12, boxShadow:'0 2px 16px rgba(6,121,255,.08)', padding:'32px 36px', marginBottom:18, border:'1px solid #e8f1ff' },
  cardTitle:{ fontFamily:"'Montserrat',sans-serif", fontSize:12, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:'#0679ff', marginBottom:4 },
  cardSub:  { fontSize:14, color:'#5e748f', marginBottom:20, lineHeight:1.5 },
  lbl:      { display:'block', fontSize:13, fontWeight:600, color:'#2e3f55', marginBottom:8 },
  textarea: { width:'100%', background:'#f5f7fa', border:'1.5px solid #dde3ed', borderRadius:8, color:'#111c2d', fontFamily:"'Open Sans',sans-serif", fontSize:14, lineHeight:1.7, padding:'13px 16px', resize:'vertical', outline:'none', transition:'border-color .2s,box-shadow .2s', marginBottom:6 },
  err:      { color:'#c0392b', fontSize:13, margin:'0 0 14px', background:'#fdf3f2', border:'1px solid #f5c6c2', borderRadius:7, padding:'10px 14px' },
  btn:      { width:'100%', background:'#0679ff', color:'#fff', border:'none', borderRadius:8, padding:'14px 28px', fontSize:15, fontWeight:700, fontFamily:"'Open Sans',sans-serif", display:'flex', alignItems:'center', justifyContent:'center', gap:10, transition:'background .2s,transform .15s,box-shadow .2s', boxShadow:'0 3px 12px rgba(6,121,255,.35)' },
  hint:     { textAlign:'center', color:'#8fa0bb', fontSize:12, margin:'9px 0 0' },
  qrHint:   { display:'flex', alignItems:'center', gap:10, marginTop:16, padding:'12px 16px', background:'#f5f7fa', border:'1px dashed #dde3ed', borderRadius:8 },
  spinner:  { width:18, height:18, border:'2.5px solid rgba(255,255,255,.35)', borderTop:'2.5px solid #fff', borderRadius:'50%', animation:'spin .7s linear infinite', display:'inline-block' },
  okBadge:  { display:'inline-flex', alignItems:'center', gap:7, fontSize:11, fontWeight:700, color:'#1a9e5c', background:'#e6f7ef', border:'1px solid #a3dfc0', borderRadius:999, padding:'4px 13px', letterSpacing:'.05em', textTransform:'uppercase' },
  okDot:    { width:6, height:6, borderRadius:'50%', background:'#1a9e5c', display:'inline-block', marginRight:2 },
  urlRow:   { display:'flex', alignItems:'center', gap:12, flexWrap:'wrap', marginBottom:14, padding:'13px 16px', background:'#f5f7fa', borderRadius:8, border:'1px solid #e8f1ff' },
  shortUrl: { fontFamily:"'Montserrat',sans-serif", fontSize:17, fontWeight:700, color:'#0679ff', flex:1, wordBreak:'break-all' },
  copyBtn:  { border:'none', color:'#fff', borderRadius:7, padding:'8px 15px', fontSize:13, fontWeight:600, fontFamily:"'Open Sans',sans-serif", cursor:'pointer', display:'flex', alignItems:'center', gap:6, whiteSpace:'nowrap', transition:'all .2s', flexShrink:0 },
  origLbl:  { fontSize:11, fontWeight:700, color:'#8fa0bb', letterSpacing:'.08em', textTransform:'uppercase', whiteSpace:'nowrap', paddingTop:2, minWidth:56 },
  origUrl:  { fontSize:13, color:'#5e748f', wordBreak:'break-all', lineHeight:1.6 },
  statsRow: { display:'flex', marginTop:16, paddingTop:16, borderTop:'1px solid #e8f1ff', flexWrap:'wrap' },
  stat:     { display:'flex', flexDirection:'column', gap:2, paddingRight:24, marginRight:24, borderRight:'1px solid #e8f1ff' },
  statLbl:  { fontSize:10, fontWeight:700, color:'#8fa0bb', letterSpacing:'.12em', textTransform:'uppercase' },
  statVal:  { fontSize:15, fontWeight:700, color:'#2e3f55' },
  footer:   { background:'#0560cc', color:'rgba(255,255,255,.55)', textAlign:'center', padding:'28px 24px', fontSize:13, marginTop:48 },
};
